import {
  Card,
  SubscriptionType,
  publishEvent
} from '@uma/react-commons/dist/index';
import { IconCreditCard } from '@veneer/core';
import React from 'react';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import {
  CardContainer,
  ContainerPaymentHistory,
  EmptyContent,
  History,
  HistoryContent,
  Link
} from 'src/styles/styles';
import {
  HpAllInAddPaymentMethodHyperLinkClicked,
  InstantInkAddPaymentMethodHyperLinkClicked,
  PaymentHistoryAllInHyperLinkClicked,
  PaymentHistoryInstantInkHyperLinkClicked
} from 'src/utils/analytics';
import { formatDateDay, formatOrdinalAbreviation } from 'src/utils/masks';
import { URLS } from 'src/utils/urls';
import { getResourceId } from 'src/utils/utils';
import { SubscriptionsCardProps } from '../SubscriptionsCard/types';
import PaymentFlag from './PaymentFlag';
import {
  getCurrencySymbol,
  isCurrencyValid,
  isPriceValid
} from 'src/utils/currency';

export const Payment = ({ subscription }: SubscriptionsCardProps) => {
  const { translate, northboundAPIs } = useDependencyManagerContext();

  const { locale, language } = northboundAPIs.v1.localization;

  const isEnglish = locale === 'en-US' || language == 'en';

  const resourceId = getResourceId(subscription);

  const billedDay = formatDateDay(
    subscription?.billing?.nextBillingCycle?.chargeDate
  );

  const paymentHistoryLink =
    subscription?.type === SubscriptionType.HP_ALL_IN
      ? URLS.AccountHistoryAllIn
      : URLS.AccountHistoryInk;

  const noPayments = (
    <EmptyContent>
      <IconCreditCard />
      <div className="caption">
        <a
          data-testid="AddPaymentMethodLink"
          href={URLS.UnifiedShippingBillingURL}
          target="_blank"
          rel="noreferrer"
          onClick={() =>
            publishEvent(
              subscription?.type === SubscriptionType.HP_ALL_IN
                ? HpAllInAddPaymentMethodHyperLinkClicked(
                    subscription?.resourceId,
                    subscription?.startDate
                  )
                : InstantInkAddPaymentMethodHyperLinkClicked(
                    subscription?.resourceId,
                    subscription?.startDate
                  )
            )
          }
        >
          {`${translate('subscriptions.addPaymentMethod', 'Add payment method')}`}
        </a>
      </div>
    </EmptyContent>
  );

  const isPriceAndCurrencyValid =
    isPriceValid(subscription?.price?.price) &&
    isCurrencyValid(subscription?.price?.currency);

  const payments = (
    <CardContainer>
      <PaymentFlag
        {...subscription?.billing?.paymentMethod}
        id={resourceId}
        startDate={subscription?.startDate}
        subscriptionType={subscription?.type}
      />
      <History>
        <HistoryContent data-testid="HistoryContent">
          {isPriceAndCurrencyValid && (
            <div>
              <b className="caption">
                {getCurrencySymbol({
                  locale,
                  currencyCode: subscription?.price?.currency
                })}
                {subscription?.price?.price}
              </b>
              {` ${translate('subscriptions.taxesMayApply', '(taxes may apply)')}`}
            </div>
          )}
          {billedDay && (
            <div className="caption">
              {`${translate(
                'subscriptions.billedMonthly',
                'billed monthly on the'
              )} ${billedDay}${isEnglish ? formatOrdinalAbreviation(billedDay) : ''}`}
            </div>
          )}
        </HistoryContent>
        <ContainerPaymentHistory className="caption">
          <Link
            data-testid="PaymentHistoryLink"
            href={paymentHistoryLink}
            target="_blank"
            rel="noreferrer"
            onClick={() =>
              publishEvent(
                subscription?.type === SubscriptionType.HP_ALL_IN
                  ? PaymentHistoryAllInHyperLinkClicked(
                      resourceId,
                      subscription?.startDate
                    )
                  : PaymentHistoryInstantInkHyperLinkClicked(
                      resourceId,
                      subscription?.startDate
                    )
              )
            }
          >
            {`${translate('subscriptions.paymentHistory', 'Payment history')}`}
          </Link>
        </ContainerPaymentHistory>
      </History>
    </CardContainer>
  );

  return (
    <Card
      title={{
        content: translate('subscriptions.payment', 'Payment') as string
      }}
      data-testid="payment-section"
    >
      {subscription?.billing?.paymentMethod?.type ? payments : noPayments}
    </Card>
  );
};
